import React, { useState } from 'react';
import { Calculator, RefreshCw } from 'lucide-react';
import { usePricing } from '@context/PricingContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';

const PricingPreview: React.FC = () => {
  const { configuration, preview, calculatePreview, clearPreview } = usePricing();
  const [eventType, setEventType] = useState('wedding');
  const [photoCount, setPhotoCount] = useState(10);
  const [promoCode, setPromoCode] = useState('');

  const handleCalculate = () => {
    calculatePreview(eventType, photoCount, promoCode || undefined);
  };

  const handleClear = () => {
    clearPreview();
    setEventType('wedding');
    setPhotoCount(10);
    setPromoCode('');
  };

  if (!configuration) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <Calculator className="h-8 w-8 mx-auto mb-2" />
          <p>Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Calculator className="h-5 w-5 text-blue-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Pricing Preview</h2>
        </div>
        <p className="text-sm text-gray-600 mt-1">Test your pricing with different scenarios.</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Input Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={eventType}
              onChange={e => setEventType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {configuration.eventTypeMultipliers.map(mult => (
                <option key={mult.eventType} value={mult.eventType}>
                  {mult.displayName} ({mult.multiplier}x)
                </option>
              ))}
            </select>
          </div>

          <div>
            <Input
              label="Number of Photos"
              type="number"
              min="1"
              max="1000"
              value={photoCount.toString()}
              onChange={e => setPhotoCount(parseInt(e.target.value) || 1)}
              placeholder="10"
            />
          </div>

          <div>
            <Input
              label="Promo Code (Optional)"
              type="text"
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase())}
              placeholder="SAVE10"
            />
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={handleCalculate}
              className="flex-1"
              icon={<Calculator className="h-4 w-4" />}
            >
              Calculate Price
            </Button>
            <Button
              variant="outline"
              onClick={handleClear}
              icon={<RefreshCw className="h-4 w-4" />}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Results */}
        {preview && (
          <div className="border-t pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Price Breakdown</h3>

            <div className="space-y-3">
              {/* Base Price */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Base Price ({preview.photoCount} photos)</span>
                <span className="font-medium">{formatCurrency(preview.basePrice)}</span>
              </div>

              {/* Event Multiplier */}
              {preview.eventMultiplier !== 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Event Type Adjustment ({preview.eventType})</span>
                  <span
                    className={`font-medium ${preview.eventMultiplier > 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {preview.eventMultiplier > 0 ? '+' : ''}
                    {formatCurrency(preview.eventMultiplier)}
                  </span>
                </div>
              )}

              {/* Volume Discount */}
              {preview.volumeDiscount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Volume Discount</span>
                  <span className="font-medium text-green-600">
                    -{formatCurrency(preview.volumeDiscount)}
                  </span>
                </div>
              )}

              {/* Special Discount */}
              {preview.specialDiscount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Promo Code Discount</span>
                  <span className="font-medium text-green-600">
                    -{formatCurrency(preview.specialDiscount)}
                  </span>
                </div>
              )}

              {/* Subtotal Line */}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between items-center font-medium">
                  <span className="text-gray-900">Customer Pays</span>
                  <span className="text-xl text-gray-900">
                    {formatCurrency(preview.finalPrice)}
                  </span>
                </div>
              </div>

              {/* Photographer Earnings */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-green-700 font-medium">Your Earnings</span>
                  <span className="text-xl font-bold text-green-700">
                    {formatCurrency(preview.photographerEarning)}
                  </span>
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {Math.round(configuration.photographerCommission * 100)}% commission (
                  {formatCurrency(preview.finalPrice - preview.photographerEarning)} platform fee)
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Comparison */}
        {configuration.basePrices && (
          <div className="border-t pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Bundle Comparison</h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Single Photo</span>
                <span>{formatCurrency(configuration.basePrices.singlePhoto)}</span>
              </div>
              <div className="flex justify-between">
                <span>5 Photos (Bundle)</span>
                <span>{formatCurrency(configuration.basePrices.smallBundle)}</span>
              </div>
              <div className="flex justify-between">
                <span>10 Photos (Bundle)</span>
                <span>{formatCurrency(configuration.basePrices.mediumBundle)}</span>
              </div>
              <div className="flex justify-between">
                <span>20 Photos (Bundle)</span>
                <span>{formatCurrency(configuration.basePrices.largeBundle)}</span>
              </div>
              <div className="flex justify-between font-medium text-blue-600">
                <span>All Photos (Best Value)</span>
                <span>{formatCurrency(configuration.basePrices.fullEvent)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Quick Stats</h3>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-600">Active Discounts</div>
              <div className="font-medium">{configuration.volumeDiscounts.length} tiers</div>
            </div>
            <div>
              <div className="text-gray-600">Event Types</div>
              <div className="font-medium">{configuration.eventTypeMultipliers.length} types</div>
            </div>
            <div>
              <div className="text-gray-600">Promo Codes</div>
              <div className="font-medium">
                {configuration.specialOffers.filter(o => o.isActive).length} active
              </div>
            </div>
            <div>
              <div className="text-gray-600">Commission Rate</div>
              <div className="font-medium">
                {Math.round(configuration.photographerCommission * 100)}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPreview;
